import React from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';

interface VulnerabilityDetail {
  id: number;
  type: string;
  severity: string;
  description: string;
  location: string;
  impact: string;
  recommendation: string;
}

const VulnerabilityReport: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const [report, setReport] = React.useState<VulnerabilityDetail[]>([]);

  React.useEffect(() => {
    // TODO: Fetch actual report data from API
    const mockReport: VulnerabilityDetail[] = [
      {
        id: 1,
        type: 'Reentrancy',
        severity: 'Critical',
        description: 'External call before state update',
        location: 'withdraw() L45',
        impact: 'Potential funds drain through recursive calls',
        recommendation: 'Implement checks-effects-interactions pattern'
      },
      {
        id: 2,
        type: 'Integer Overflow',
        severity: 'High',
        description: 'Unchecked arithmetic operation',
        location: 'transfer() L67',
        impact: 'Potential balance manipulation',
        recommendation: 'Use SafeMath or Solidity 0.8+ built-in checks'
      }
    ];
    setReport(mockReport);
  }, [contractId]);

  const columns: GridColDef[] = [
    { field: 'type', headerName: 'Type', width: 150 },
    { field: 'severity', headerName: 'Severity', width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value}
          color={params.value === 'Critical' ? 'error' : 
                params.value === 'High' ? 'warning' : 'info'}
        />
      )
    },
    { field: 'description', headerName: 'Description', width: 250 },
    { field: 'location', headerName: 'Location', width: 150 },
    { field: 'impact', headerName: 'Impact', width: 250 },
    { field: 'recommendation', headerName: 'Recommendation', width: 300 }
  ];

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            Vulnerability Report - Contract #{contractId}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Identified Vulnerabilities
            </Typography>
            <div style={{ height: 400, width: '100%' }}>
              <DataGrid
                rows={report}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 5 },
                  },
                }}
                pageSizeOptions={[5, 10]}
                disableRowSelectionOnClick
              />
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary={`Total Vulnerabilities: ${report.length}`}
                  secondary={`Critical: ${report.filter(v => v.severity === 'Critical').length}, 
                             High: ${report.filter(v => v.severity === 'High').length}`}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VulnerabilityReport;